import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Mail, ChevronRight, User, Menu, LogOut, MapPin, Send, Phone, HelpCircle, Search, Users, Star, Receipt, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../mock/data';
import axios from 'axios';
import ServiceCard from '../components/ServiceCard';
import BookingCard from '../components/BookingCard';
import StepByStepBookingModal from '../components/StepByStepBookingModal';
import VisaStatusModal from '../components/VisaStatusModal';
import ServiceStepsModal from '../components/ServiceStepsModal';
import MobileMenu from '../components/MobileMenu';
import NotificationBell from '../components/NotificationBell';
import MobileBottomNav from '../components/MobileBottomNav';
import BusinessBookingModal from '../components/BusinessBookingModal';
import ReviewModal from '../components/ReviewModal';
import StarRating from '../components/StarRating';
import FloatingContact from '../components/FloatingContact';
import StickyContactBox from '../components/StickyContactBox';
import FloatingChat from '../components/FloatingChat';
import SiteFooter from '../components/SiteFooter';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import ChatAI from '../components/ChatAI';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import WelcomeSplash from '../components/WelcomeSplash';
import { useTheme } from '../context/ThemeContext';
import VisaChecker from '../components/VisaChecker';
import Stories from '../components/Stories';
import ToolSidebar from '../components/ToolSidebar';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import '../styles/HomePage.css';
import '../styles/LuxeDubaiHero.css';
import '../styles/AuthPages.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback data in case API fails
// Text Section Banner Component - supports multi-language
const TextSectionBanner = ({ section, currentLang }) => {
  // Get text based on current language, fallback to English
  const getText = () => {
    if (currentLang === 'ru' && section.text_ru) return section.text_ru;
    if (currentLang === 'uz' && section.text_uz) return section.text_uz;
    return section.text_en || section.text || '';
  };
  
  const text = getText();
  if (!text) return null;
  
  return (
    <div className="text-section-banner">
      <div className="text-section-banner-line"></div>
      <span className="text-section-banner-text">{text}</span>
      <div className="text-section-banner-line"></div>
    </div>
  );
};

const HomePage = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  const seo = useSEO('home', {
    title: 'Registan Tourism | Dubai Visa & Travel Services',
    description: 'Apply for Dubai visa online. Tourist visa 14, 30, 60 days, visa change, freelance visa & business setup in UAE. Fast processing from AED 350.',
    keywords: 'Dubai visa, UAE visa, tourist visa Dubai, visa change Dubai, Dubai visa online, UAE tourist visa, freelance visa UAE, start business Dubai'
  });
  
  const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [activeSection, setActiveSection] = useState('services');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isVisaStatusOpen, setIsVisaStatusOpen] = useState(false);
  const [isChatAIOpen, setIsChatAIOpen] = useState(false);

  // Listen for open-chatai event from mobile bottom nav
  useEffect(() => {
    const handler = () => setIsChatAIOpen(true);
    window.addEventListener('open-chatai', handler);
    return () => window.removeEventListener('open-chatai', handler);
  }, []);

  // Listen for open-visa-status event from mobile bottom nav
  useEffect(() => {
    const handler = () => setIsVisaStatusOpen(true);
    window.addEventListener('open-visa-status', handler);
    return () => window.removeEventListener('open-visa-status', handler);
  }, []);

  const [stepsModalService, setStepsModalService] = useState(null);
  
  // Services from backend
  const FALLBACK_SERVICES = [
    { id: '1771606954.247215', name: 'Dubai Visa', subtitle: 'Tourist Visa', duration: '3-5 Days', price: 'AED 350', description: 'Tourist visa for Dubai', icon: 'Plane', position: 0 },
    { id: '1773177615.488674', name: 'Visa Change', subtitle: 'Visa Change', duration: '2-5', price: '750', description: 'Change your UAE visa quickly and safely', icon: 'RefreshCw', position: 1 },
    { id: '4', name: 'Freelance Visa', subtitle: 'Freelance Visa', duration: '5-7 Days', price: 'AED 5,000', description: 'Get your freelance visa', icon: 'Briefcase', position: 2 },
    { id: '1772657688.20537', name: 'Family Visa', subtitle: 'Family Visa', duration: '7-10 days', price: 'AED 4500', description: 'Family Visa in the UAE', icon: 'Users', position: 3 },
  ];

  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [loadingServices, setLoadingServices] = useState(true);
  const [serviceCategory, setServiceCategory] = useState('all');
  const [businessModalService, setBusinessModalService] = useState(null); // holds the selected Business service
  
  // Text sections from backend
  const [textSections, setTextSections] = useState([]);
  
  // Reviews from backend
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [reviewPage, setReviewPage] = useState(0);
  const REVIEWS_PER_PAGE = 5;

  // Fetch services from backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/services`);
        if (response.data && response.data.length > 0) {
          setServices(response.data);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        // Fallback services already set in initial state
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  // Fetch text sections from backend
  useEffect(() => {
    const fetchTextSections = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/text-sections`);
        if (response.data) {
          setTextSections(response.data);
        }
      } catch (error) {
        console.error('Error fetching text sections:', error);
      }
    };
    fetchTextSections();
  }, []);

  // Fetch reviews from backend
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/reviews`);
        if (response.data) {
          setReviews(response.data);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBookService = (service) => {
    // Business cards open the in-page 8-step modal instead of navigating
    if (service?.zone === 'freezone' || service?.zone === 'mainland') {
      setBusinessModalService(service);
      return;
    }
    if (service?.navigateTo) {
      navigate(service.navigateTo);
      return;
    }
    setSelectedService(service);
    setIsBookingModalOpen(true);
  };

  const handleBookingSubmit = (formData) => {
    console.log('Booking submitted:', formData);
    alert(`Thank you ${formData.name}! Your booking for ${formData.service} has been received. We will contact you on WhatsApp (${formData.mobile}) within 24 hours.`);
    setIsBookingModalOpen(false);
    setSelectedService(null);
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/reviews`,
        {
          author: reviewData.author,
          rating: reviewData.rating,
          text: reviewData.text,
          image_url: reviewData.image_url || null,
          video_url: reviewData.video_url || null,
          avatar_url: reviewData.avatar_url || null,
          instagram: reviewData.instagram || null,
          tiktok: reviewData.tiktok || null
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        // Refresh reviews from backend
        const reviewsResponse = await axios.get(`${BACKEND_URL}/api/reviews`);
        setReviews(reviewsResponse.data);
        setIsReviewModalOpen(false);
        alert('Thank you for your review!');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response?.status === 401) {
        alert('Please login to leave a review');
      } else {
        alert('Failed to submit review. Please try again.');
      }
    }
  };

  return (
    <div className="homepage">
      {showSplash && <WelcomeSplash onComplete={() => setShowSplash(false)} />}
      <Helmet>
        <link rel="canonical" href="https://registantours.com" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Header */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo">
            <img src={currentLogo} alt="Registan" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
          <button 
            className="desktop-visa-status-btn"
            onClick={() => setIsVisaStatusOpen(true)}
            data-testid="desktop-visa-status-btn"
            style={{
              display: 'none',
              alignItems: 'center', gap: '6px',
              padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--brand-primary, #2E7D32)',
              background: 'rgba(46, 125, 50, 0.1)', color: 'var(--brand-primary, #4CAF50)',
              whiteSpace: 'nowrap',
            }}
          >
            <Search size={14} />
            Visa Status
          </button>
          <button className="mobile-chatai-btn" onClick={() => setIsChatAIOpen(true)} data-testid="mobile-chatai-btn">
            <MessageSquare size={16} />
            <span>Chat AI</span>
          </button>
          <button 
            className="my-transactions-btn"
            onClick={() => navigate('/dashboard')}
            data-testid="my-transactions-btn"
          >
            <Receipt size={16} />
            <span>{t('myTransactions') || 'My Transactions'}</span>
          </button>
          <button 
            className="my-transactions-btn chatai-header-btn"
            onClick={() => setIsChatAIOpen(true)}
            data-testid="chat-ai-btn"
          >
            <MessageSquare size={16} />
            <span>{t('chatAI') || 'Chat AI'}</span>
          </button>
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <a 
            href="#services" 
            className={`dark-nav-link ${activeSection === 'services' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            {t('home') || 'Home'}
          </a>
          <Link 
            to="/start-business"
            className="dark-nav-link"
            data-testid="start-business-nav-btn"
          >
            {t('startBusiness') || 'Start Business'}
          </Link>
          <Link to="/tourismsoftware" className="dark-nav-link" data-testid="tourism-software-nav-btn">Tourism Software</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
          <Link to="/tours" className="dark-nav-link">Tours</Link>
        </nav>
        {/* Phone removed per user request */}
        
        {isAuthenticated ? (
          <div className="header-user-menu">
            <NotificationBell userEmail={user?.email} />
            <button className="btn-user-profile" onClick={() => window.location.href = '/dashboard'}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => window.location.href = '/login'}>
              {t('login')}
            </button>
            <button className="btn-primary btn-signup" onClick={() => window.location.href = '/register'}>
              {t('signUp')}
            </button>
          </div>
        )}
        
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/" />

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        activeSection={activeSection}
        onNavigate={scrollToSection}
      />

      <div className="tool-page-layout">
        <ToolSidebar />
        <div className="tool-page-content">
      {/* Hero Section - Dubai Smart System */}
      <section className="hero-section hero-animated">
        <div className="hero-animation-container">
          {/* === Luxury Blue-Hour Dubai Hero (real photography) === */}
          <div className="hero-bg-pro hero-bg-luxe" aria-hidden="true">
            {/* Photography with slow Ken Burns pan */}
            <div className="hero-luxe-photo" />

            {/* Cinematic overlays */}
            <div className="hero-luxe-tint" />
            <div className="hero-luxe-vignette" />
            <div className="hero-luxe-grain" />

            {/* Elegant gold horizon light */}
            <div className="hero-luxe-horizon-line" />

            {/* Soft light sweep across the frame */}
            <div className="hero-luxe-lightsweep" />

            {/* Slow gold ambient particles */}
            <div className="hero-luxe-particles">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  className="hero-luxe-particle"
                  style={{
                    left: `${(i * 7.5 + 3) % 100}%`,
                    '--dur': `${18 + (i % 5) * 3}s`,
                    '--delay': `${(i * 1.7) % 14}s`,
                    '--drift': `${(i % 2 === 0 ? 1 : -1) * (20 + (i % 4) * 8)}px`,
                  }}
                />
              ))}
            </div>

            {/* Cinematic airplane silhouette */}
            <svg className="hero-luxe-plane-svg" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
              <defs>
                <linearGradient id="luxePathGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#e6c382" stopOpacity="0" />
                  <stop offset="45%" stopColor="#e6c382" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="#e6c382" stopOpacity="0" />
                </linearGradient>
                <symbol id="luxePlane" viewBox="0 0 24 24">
                  <path className="hero-luxe-plane-body" d="M2 12 L9 11 L11 4 L13 4 L12 11 L22 12 L12 13 L13 20 L11 20 L9 13 Z" />
                </symbol>
                <path id="luxePathDef" d="M -50,260 Q 400,140 900,220 T 1700,340" />
              </defs>
              <use href="#luxePathDef" stroke="url(#luxePathGrad)" strokeWidth="1" strokeDasharray="4 12" fill="none" />
              <g>
                <use href="#luxePlane" width="18" height="18" x="-9" y="-9" />
                <animateMotion dur="38s" repeatCount="indefinite" rotate="auto" begin="5s">
                  <mpath href="#luxePathDef" />
                </animateMotion>
              </g>
            </svg>

            {/* Bottom fade for text legibility */}
            <div className="hero-luxe-bottomfade" />
          </div>

          {/* Central content */}
          <div className="hero-center-content">
            <div className="animated-logo-container hero-logo-v2">
              {/* Outer tick ring */}
              <svg className="logo-tick-ring" viewBox="0 0 200 200" aria-hidden="true">
                <defs>
                  <linearGradient id="heroArcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#5EFCE8" stopOpacity="0" />
                    <stop offset="60%" stopColor="#5EFCE8" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#7c6cff" stopOpacity="1" />
                  </linearGradient>
                </defs>
                <circle cx="100" cy="100" r="94" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="1" />
                {Array.from({ length: 48 }).map((_, i) => {
                  const angle = (i * 360) / 48;
                  const rad = (angle * Math.PI) / 180;
                  const x1 = 100 + Math.cos(rad) * 94;
                  const y1 = 100 + Math.sin(rad) * 94;
                  const x2 = 100 + Math.cos(rad) * (i % 4 === 0 ? 85 : 90);
                  const y2 = 100 + Math.sin(rad) * (i % 4 === 0 ? 85 : 90);
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeOpacity={i % 4 === 0 ? 0.55 : 0.25} strokeWidth="1" />;
                })}
                {/* Rotating scan arc */}
                <path
                  className="logo-scan-arc"
                  d="M 100 100 L 100 6 A 94 94 0 0 1 180.4 59 Z"
                  fill="url(#heroArcGrad)"
                  opacity="0.22"
                />
              </svg>
              {/* Orbiting dot */}
              <div className="logo-orbit">
                <span className="logo-orbit-dot" />
              </div>
              {/* Inner decorative ring */}
              <div className="logo-ring logo-ring-inner" />
              <div className="logo-center">
                <img src={currentLogo} alt="R" className="logo-center-img" />
              </div>
            </div>
            <h1 className="hero-title hero-title-v2">
              {t('heroTitle').split('').map((c, i) => (
                <span
                  key={i}
                  className={`hero-title-char${c === ' ' ? ' is-space' : ''}`}
                  style={{ animationDelay: `${0.1 + i * 0.04}s` }}
                >{c === ' ' ? '\u00A0' : c}</span>
              ))}
            </h1>
            <p className="hero-subtitle hero-subtitle-v2">
              <span className="hero-subtitle-inner">{t('heroSubtitle')}</span>
              <span className="hero-subtitle-underline" aria-hidden="true" />
            </p>
            
            {/* Visa Checker in Hero */}
            <VisaChecker compact={true} />
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">500+</span>
                <span className="stat-label">{t('visasProcessed')}</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">24h</span>
                <span className="stat-label">{t('fastProcessing')}</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">100%</span>
                <span className="stat-label">{t('successRate')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="main-content">
        <div className="content-left">
          {/* Services Section */}
          <section id="services" className="content-section">
            <div id="dubai-visa">
              {/* Text sections above Stories */}
              {textSections.filter(s => s.placement === 'stories' || !s.placement).length > 0 && (
                <div className="text-sections-inline">
                  {textSections.filter(s => s.placement === 'stories' || !s.placement).map((section) => (
                    <TextSectionBanner key={section.id} section={section} currentLang={language} />
                  ))}
                </div>
              )}
              
              {/* Instagram-style Stories */}
              <Stories />
              
              {/* Text sections above Services */}
              {textSections.filter(s => s.placement === 'services').length > 0 && (
                <div className="text-sections-inline">
                  {textSections.filter(s => s.placement === 'services').map((section) => (
                    <TextSectionBanner key={section.id} section={section} currentLang={language} />
                  ))}
                </div>
              )}
              
              <h1 className="section-title-glow">{t('ourServices')}</h1>

              {/* Category filter tabs */}
              <div className="services-category-tabs" data-testid="services-category-tabs">
                {[
                  { key: 'all', label: t('catAll') || 'All' },
                  { key: 'visa', label: t('catVisa') || 'Visa' },
                  { key: 'business', label: t('catBusiness') || 'Business' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    className={`services-cat-pill ${serviceCategory === tab.key ? 'is-active' : ''}`}
                    onClick={() => setServiceCategory(tab.key)}
                    data-testid={`cat-pill-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="services-grid">
                {services.filter((service) => {
                  if (serviceCategory === 'all') return true;
                  const txt = `${service.category || ''} ${service.name || ''} ${service.subtitle || ''}`.toLowerCase();
                  const isBusiness = /business|trade license|mainland|freezone|free zone|company|llc|licence/.test(txt);
                  return serviceCategory === 'business' ? isBusiness : !isBusiness;
                }).map((service) => (
                  <ServiceCard 
                    key={service.id} 
                    service={service} 
                    onBookClick={handleBookService}
                    onStepsClick={(service) => service.navigateTo ? navigate(service.navigateTo) : setStepsModalService(service)}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Reviews Section */}
          <section id="reviews" className="content-section">
            {/* Text sections above Reviews */}
            {textSections.filter(s => s.placement === 'reviews').length > 0 && (
              <div className="text-sections-inline">
                {textSections.filter(s => s.placement === 'reviews').map((section) => (
                  <TextSectionBanner key={section.id} section={section} currentLang={language} />
                ))}
              </div>
            )}
            
            <div className="reviews-header">
              <h2 className="section-title">{t('customerReviews')}</h2>
              <button 
                className="btn-primary btn-leave-review"
                onClick={() => setIsReviewModalOpen(true)}
              >
                {t('leaveReview')}
              </button>
            </div>
            <div className="reviews-list">
              {reviews
                .slice(reviewPage * REVIEWS_PER_PAGE, (reviewPage + 1) * REVIEWS_PER_PAGE)
                .map((review) => {
                  // Handle review attachment (image or video)
                  const resolveUrl = (url) => url ? (url.startsWith('http') ? url : `${BACKEND_URL}${url}`) : null;
                  const attachmentUrl = resolveUrl(review.image_url) || resolveUrl(review.video_url);
                  const isVideo = !!review.video_url;
                  const avatarUrl = resolveUrl(review.avatar_url);
                  
                  return (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={review.author} className="reviewer-avatar-img" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div className="reviewer-avatar" style={{ display: avatarUrl ? 'none' : 'flex' }}>{review.author.charAt(0)}</div>
                    <div className="reviewer-info">
                      <h4>{review.author}</h4>
                      <StarRating rating={review.rating} readonly={true} />
                      {(review.instagram || review.tiktok) && (
                        <div className="reviewer-socials">
                          {review.instagram && (
                            <a href={`https://instagram.com/${review.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="social-link instagram">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                            </a>
                          )}
                          {review.tiktok && (
                            <a href={`https://tiktok.com/@${review.tiktok.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="social-link tiktok">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="review-date">{review.date}</span>
                  </div>
                  <p className="review-text">{review.text}</p>
                  {attachmentUrl && (
                    <div className={`review-attachment-media ${isVideo ? 'video-wrap' : ''}`} data-testid="review-attachment">
                      {isVideo ? (
                        <video 
                          src={attachmentUrl} 
                          controls 
                          preload="metadata" 
                          controlsList="nodownload nofullscreen noremoteplayback"
                          disablePictureInPicture
                          onContextMenu={(e) => e.preventDefault()}
                          className="review-attachment-video"
                        />
                      ) : (
                        <img src={attachmentUrl} alt={`${review.author}'s experience`} className="review-attachment-img" onClick={() => window.open(attachmentUrl, '_blank')} onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                      )}
                    </div>
                  )}
                </div>
              )})}
            </div>
            
            {/* Reviews Pagination */}
            {reviews.length > REVIEWS_PER_PAGE && (
              <div className="reviews-pagination">
                <button 
                  className="btn-pagination"
                  onClick={() => setReviewPage(prev => prev - 1)}
                  disabled={reviewPage === 0}
                >
                  <ChevronRight size={18} style={{ transform: 'rotate(180deg)' }} />
                  {t('previous') || 'Previous'}
                </button>
                <span className="pagination-info">
                  {reviewPage + 1} / {Math.ceil(reviews.length / REVIEWS_PER_PAGE)}
                </span>
                <button 
                  className="btn-pagination"
                  onClick={() => setReviewPage(prev => prev + 1)}
                  disabled={(reviewPage + 1) * REVIEWS_PER_PAGE >= reviews.length}
                >
                  {t('next') || 'Next'}
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </section>

          {/* SEO Content Section */}
          <section className="seo-content-section">
            <article className="seo-article">
              <h2 className="seo-heading">{t('seoDubaiTitle')}</h2>
              <p>{t('seoDubaiP1')}</p>
              <p>{t('seoDubaiP2')}</p>
              <p>{t('seoDubaiP3')}</p>
            </article>
            <article className="seo-article">
              <h2 className="seo-heading">{t('seoVisaChangeTitle')}</h2>
              <p>{t('seoVisaChangeP1')}</p>
              <p>{t('seoVisaChangeP2')}</p>
              <p>{t('seoVisaChangeP3')}</p>
            </article>
          </section>
        </div>

      </div>

      <SiteFooter scrollToSection={scrollToSection} />
        </div>
      </div>

      {/* Booking Modal */}
      {isBookingModalOpen && (
        <StepByStepBookingModal 
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedService(null);
          }}
          selectedService={selectedService}
        />
      )}

      {/* Visa Status Modal */}
      {isVisaStatusOpen && (
        <VisaStatusModal 
          onClose={() => setIsVisaStatusOpen(false)}
        />
      )}

      {/* Review Modal */}
      {isReviewModalOpen && (
        <ReviewModal
          onClose={() => setIsReviewModalOpen(false)}
          onSubmit={handleReviewSubmit}
        />
      )}

      {/* Service Steps Modal */}
      {stepsModalService && (
        <ServiceStepsModal
          service={stepsModalService}
          onClose={() => setStepsModalService(null)}
        />
      )}

      {/* Floating Contact Button - Mobile Only */}
      <StickyContactBox />
      
      {/* Floating Chat Button */}
      <FloatingChat />
      <ChatAI isOpen={isChatAIOpen} onClose={() => setIsChatAIOpen(false)} />
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* In-page 8-step Business Application Modal */}
      <BusinessBookingModal
        isOpen={!!businessModalService}
        zone={businessModalService?.zone || 'freezone'}
        service={businessModalService}
        onClose={() => setBusinessModalService(null)}
      />
    </div>
  );
};

export default HomePage;
